import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import { alpha, useTheme } from '@mui/material/styles'

import type { HotelDto } from '../../types/api'
import { useFormatters } from '../../hooks/useFormatters'

const BAND_COLOR: Record<string, 'default' | 'success' | 'info' | 'secondary'> = {
  BUDGET: 'success',
  MID: 'info',
  UPSCALE: 'secondary',
  LUXURY: 'secondary',
}

/**
 * A hotel recommendation.
 *
 * The owner's note is given more visual weight than the description, because it is the only
 * thing on the card a guest cannot get from an OTA. "Book this one for the arrival night,
 * not needing a taxi is worth more than a nicer room" is the reason this page exists at all.
 */
export function HotelCard({ hotel }: { hotel: HotelDto }) {
  const { t } = useTranslation()
  const { money } = useFormatters()
  const theme = useTheme()

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <Chip size="small" color={BAND_COLOR[hotel.priceBand]} label={t(`hotels.${hotel.priceBand}`)} />
          {hotel.stars != null && (
            <Chip size="small" variant="outlined" label={'⭐'.repeat(hotel.stars)} />
          )}
          {hotel.area && <Chip size="small" variant="outlined" label={`📍 ${hotel.area}`} />}
        </Stack>

        <Box>
          <Typography variant="h4" component="h3">
            {hotel.name}
          </Typography>
          {hotel.priceFrom != null && (
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
              {t('hotels.priceFrom', { price: money(hotel.priceFrom) })} · {t('common.perNight')}
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {hotel.summary}
        </Typography>

        {hotel.ownerTip && (
          <Box
            sx={{
              p: 1.75,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.secondary.main, 0.09),
              borderInlineStart: `3px solid ${theme.palette.secondary.main}`,
            }}
          >
            <Typography variant="overline" sx={{ display: 'block', color: 'secondary.dark', mb: 0.25 }}>
              💡 {t('hotels.ownerTip')}
            </Typography>
            <Typography variant="body2">{hotel.ownerTip}</Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {hotel.walkToStationMinutes != null && (
            <Typography variant="caption" color="text.secondary">
              🚶 {t('hotels.walkToStation', { count: hotel.walkToStationMinutes })}
            </Typography>
          )}
          {hotel.address && (
            <Typography variant="caption" color="text.secondary">
              📍 {hotel.address}
            </Typography>
          )}
        </Stack>

        {(hotel.bookingUrl || hotel.websiteUrl) && (
          <Button
            href={hotel.bookingUrl || hotel.websiteUrl || ''}
            target="_blank"
            rel="noopener noreferrer nofollow"
            variant="outlined"
            size="small"
            endIcon={<OpenInNewIcon />}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          >
            {t('hotels.viewHotel')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
